import type { ReactNode } from 'react';

import { useAuthenticator } from '@aws-amplify/ui-react';
import { usePermissions } from '@hooks/usePermissions';
import { useUserInfo } from '@hooks/useUserInfo';
import {
  Avatar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useSidebarStore } from '@store/useSidebarStore';
import { utilityRegistry } from '@utilities/registry';
import { House, LogOut, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router';

interface SidebarProps {
  width: number;
}

interface SidebarNavItemProps {
  collapsed: boolean;
  end?: boolean;
  icon: ReactNode;
  label: string;
  to: string;
}

const SidebarNavItem = ({ collapsed, end, icon, label, to }: SidebarNavItemProps) => (
  <Tooltip placement="right" title={collapsed ? label : ''}>
    <ListItemButton
      component={NavLink}
      end={end}
      sx={theme => ({
        '&.active': {
          bgcolor: theme.palette.sidebarHover,
          borderLeft: `3px solid ${theme.palette.primary.main}`,
          color: '#fff',
          pl: collapsed ? 0.85 : 1.1,
        },
        '&:hover': { bgcolor: theme.palette.sidebarHover, color: '#fff' },
        borderRadius: 1.5,
        color: theme.palette.sidebarTextDim,
        justifyContent: collapsed ? 'center' : 'flex-start',
        mb: collapsed ? 1 : 0.25,
        px: collapsed ? 1 : 2,
        py: collapsed ? 1.25 : 1,
      })}
      to={to}
    >
      <ListItemIcon
        sx={{
          color: 'inherit',
          minWidth: collapsed ? 0 : 32,
        }}
      >
        {icon}
      </ListItemIcon>
      {!collapsed && <ListItemText primary={label} slotProps={{ primary: { fontSize: 13.5 } }} />}
    </ListItemButton>
  </Tooltip>
);

interface BrandMarkProps {
  collapsed: boolean;
  /** Omit to hide the collapse toggle (mobile drawer). */
  onToggle?: () => void;
}

const BrandMark = ({ collapsed, onToggle }: BrandMarkProps) => {
  const label = collapsed ? 'Expand sidebar' : 'Collapse sidebar';

  return (
    <Stack
      alignItems="center"
      direction={collapsed ? 'column' : 'row'}
      spacing={collapsed ? 1.5 : 1.25}
      sx={{ pb: 2, pt: 2.5, px: collapsed ? 1 : 2.5 }}
    >
      <Box alt="KAES mark" component="img" src="/favicon.svg" sx={{ height: 18, width: 19 }} />
      {!collapsed && (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: '#fff', fontSize: 16, fontWeight: 700, letterSpacing: '0.01em' }}>
            KAES
            <Typography component="sup" sx={{ color: theme => theme.palette.sidebarTextDim, fontSize: 8, ml: 0.25 }}>
              TM
            </Typography>
          </Typography>
          <Typography
            sx={{ color: theme => theme.palette.sidebarTextDim, fontSize: 10.5, letterSpacing: '0.03em', mt: '2px' }}
          >
            Ops Utility Hub
          </Typography>
        </Box>
      )}
      {onToggle && (
        <Tooltip placement="right" title={label}>
          <IconButton
            aria-label={label}
            onClick={onToggle}
            sx={theme => ({
              '&:hover': { bgcolor: theme.palette.sidebarHover, color: '#fff' },
              borderRadius: 1.5,
              color: theme.palette.sidebarTextDim,
              p: 1.25,
            })}
          >
            {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

const COLLAPSED_WIDTH = 72;

export const Sidebar = ({ width }: SidebarProps) => {
  const { grantedUtilityIds } = usePermissions();
  const { closeMobile, isCollapsed: isCollapsedPreference, isMobileOpen, toggleCollapsed } = useSidebarStore();
  const { pathname } = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isCollapsed = !isMobile && isCollapsedPreference;
  const { signOut } = useAuthenticator(context => [context.user]);
  const { userInfo } = useUserInfo();

  // Close the drawer after navigating, and when resizing across the breakpoint so it doesn't reopen later.
  useEffect(() => {
    closeMobile();
  }, [closeMobile, isMobile, pathname]);

  const visibleUtilities = utilityRegistry.filter(utility => grantedUtilityIds.includes(utility.id));
  const displayName = `${userInfo.givenName || ''} ${userInfo.familyName || ''}`.trim() || userInfo.email;
  const initials =
    userInfo.givenName && userInfo.familyName
      ? `${userInfo.givenName.charAt(0)}${userInfo.familyName.charAt(0)}`.toUpperCase()
      : userInfo.email.slice(0, 2).toUpperCase();

  const content = (
    <>
      <BrandMark collapsed={isCollapsed} onToggle={isMobile ? undefined : toggleCollapsed} />

      <List sx={{ flex: 1, px: 1.5, py: 1 }}>
        <SidebarNavItem collapsed={isCollapsed} end icon={<House size={16} />} label="Home" to="/" />
        {visibleUtilities.map(utility => (
          <SidebarNavItem
            collapsed={isCollapsed}
            icon={<utility.icon size={16} />}
            key={utility.id}
            label={utility.title}
            to={utility.path}
          />
        ))}
      </List>

      <Stack
        alignItems="center"
        direction={isCollapsed ? 'column' : 'row'}
        spacing={isCollapsed ? 1 : 1.5}
        sx={theme => ({ borderTop: `1px solid ${theme.palette.sidebarHover}`, p: 1.5 })}
      >
        <Avatar sx={{ fontSize: 11, height: 28, width: 28 }}>{initials}</Avatar>
        {!isCollapsed && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography noWrap sx={{ color: '#fff', fontWeight: 500, lineHeight: 1.25 }} variant="body2">
              {displayName}
            </Typography>
          </Box>
        )}
        <Tooltip placement="right" title={isCollapsed ? 'Sign out' : ''}>
          <IconButton
            aria-label="Sign out"
            onClick={() => {
              signOut();
            }}
            size="small"
            sx={theme => ({ color: theme.palette.sidebarTextDim })}
          >
            <LogOut size={16} />
          </IconButton>
        </Tooltip>
      </Stack>
    </>
  );

  if (isMobile) {
    return (
      <Drawer
        onClose={closeMobile}
        open={isMobileOpen}
        slotProps={{
          paper: {
            sx: theme => ({
              backgroundImage: 'none',
              bgcolor: theme.palette.sidebarBackground,
              borderTop: `3px solid ${theme.palette.primary.main}`,
              width,
            }),
          },
        }}
        variant="temporary"
      >
        <Box component="nav" sx={{ display: 'flex', flex: 1, flexDirection: 'column', minHeight: 0 }}>
          {content}
        </Box>
      </Drawer>
    );
  }

  return (
    <Box
      component="nav"
      sx={theme => ({
        bgcolor: theme.palette.sidebarBackground,
        borderTop: `3px solid ${theme.palette.primary.main}`,
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        height: '100vh',
        position: 'sticky',
        top: 0,
        transition: theme.transitions.create('width', { duration: theme.transitions.duration.shorter }),
        width: isCollapsed ? COLLAPSED_WIDTH : width,
      })}
    >
      {content}
    </Box>
  );
};
